import { routeForOrgLogin } from '@watershed/shared-universal/dashboardRoutes';
import { ORG_ID_PARAM } from '@watershed/shared-universal/utils/pinnedOrganizationUniversalConstants';

type AccessibleOrg = { id: string; canAccessInCurrentSession: boolean };

/**
 * Remove a query parameter from a URL and return the cleaned path.
 * Returns just the pathname if no other query params remain,
 * otherwise returns pathname + remaining query params.
 */
export function removeQueryParam(url: string, paramName: string): string {
  const urlObj = new URL(url);
  urlObj.searchParams.delete(paramName);
  const searchParamsString = urlObj.searchParams.toString();
  return searchParamsString.length > 0
    ? `${urlObj.pathname}?${searchParamsString}`
    : urlObj.pathname;
}

export type OrgIdParamAction =
  | { type: 'no-permission'; orgName: string } // The user does not belong to the target org
  | { type: 'switch-org'; targetOrgId: string; redirect: string } // The user belongs to the target org, so we can switch to it
  | { type: 'switch-org-reauth-required'; url: string } // The user belongs to the target org, but needs to be reauthenticated using a different auth method
  | { type: 'clean-url'; redirect: string } // The org id in the url is the current org, so we can just trim out the org id param
  | { type: 'none' }; // No action needed
export type GetOrgIdParamActionArgs = {
  urlOrgId: string | null; // The org id in the url
  currentOrgId: string; // The active organization org id
  accessibleOrgs: Array<AccessibleOrg>; // All the orgs the user belongs to
  currentUrl: string;
};

/**
 * Given an org id in the url, the current organization id,
 * and the accessible orgs the user belongs to,
 * determine if the user needs to be redirected to a different org.
 */
export function getOrgIdParamAction({
  urlOrgId,
  currentOrgId,
  accessibleOrgs,
  currentUrl,
}: GetOrgIdParamActionArgs): OrgIdParamAction {
  if (!urlOrgId) {
    return { type: 'none' };
  }

  const targetOrg = accessibleOrgs.find((org) => org.id === urlOrgId);

  if (!targetOrg) {
    return { type: 'no-permission', orgName: urlOrgId };
  }

  // Calculate clean redirect URL (without the org ID param)
  const redirectClean = removeQueryParam(currentUrl, ORG_ID_PARAM);

  const userCanAccessTargetOrgInCurrentSession =
    targetOrg.canAccessInCurrentSession;

  // We have access to the org, so we can switch to it seamlessly
  if (userCanAccessTargetOrgInCurrentSession && targetOrg.id !== currentOrgId) {
    return {
      type: 'switch-org',
      targetOrgId: targetOrg.id,
      redirect: redirectClean,
    };
  }

  // We have access to the org, but need to reauth first
  if (!userCanAccessTargetOrgInCurrentSession) {
    return {
      type: 'switch-org-reauth-required',
      url: routeForOrgLogin(targetOrg.id, { redirect: redirectClean }),
    };
  }

  // The org id in the url is the current org, so we can just trim out the org id param
  if (targetOrg.id === currentOrgId) {
    return { type: 'clean-url', redirect: redirectClean };
  }

  return { type: 'none' };
}
